/**
 * @file CompanySchedulePage.tsx
 * @brief Company Admin → Diary: the phone's Diary tab on the web.
 *
 * @details
 * DES (30-09-2026): "mobile main yeh page diary kay naam say hai, isay match
 * karo". Port of the Diary tab in mobile `CompanyAdminScreen.tsx`:
 * - Title "Diary"; no KPI boxes, no table. Search (a round field) and the
 *   date range circle (`DateRangePopover`) on one row, as the phone's dock
 *   has them; "Schedule session" on the right (the phone books from the
 *   Employees tab).
 * - Grouped like the phone: Today, Tomorrow, then by month for what is
 *   ahead (soonest first); past days below (newest first).
 * - Each session on a timeline: a 2px rail and a 16px node in the status
 *   colour; the other participant as a 20px bold title and "30 Sep at
 *   16:00"; under it a white card (radius 16) with the status pill riding
 *   its top edge, the employee in brand ("Booked by …" muted), email · ref,
 *   and the language pair with flags.
 * - Status from the time, as the phone: Upcoming, Late (within an hour),
 *   Missed, else the stored Cancelled / Completed.
 * - Clicking a session you booked opens Edit / Cancel session as a menu
 *   (the phone's long-press sheet).
 *
 * @author DES
 * @date 19-06-2026
 * @updated 29-07-2026 — flush filters + schedule FormSheet
 * @updated 30-09-2026 — the phone's Diary: timeline rows, search + dates
 */

import { HintBubble, HintIcon } from "@/components/ui/HintPopover";
import { useHintPopover } from "@/components/ui/useHintPopover";
import { useEffect, useMemo, useRef, useState, type ReactNode } from "react";
import { useSearchParams } from "react-router-dom";
import { format } from "date-fns";
import {
  ArrowRight,
  CalendarDays,
  CalendarPlus,
  CircleX,
  Clock,
  Ellipsis,
  Pencil,
  UsersRound,
  X,
} from "lucide-react";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/AlertDialog";
import { EmptyState } from "@/components/feedback/EmptyState";
import { DateRangePopover } from "@/components/ui/DateRangePopover";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuTrigger,
} from "@/components/ui/DropdownMenu";
import { LanguageFlag } from "@/components/ui/LanguageFlag";
import { FlipClock } from "@/features/employee-boardroom/FlipClock";
import { useCountdown } from "@/shared/utils/countdown";
import { ApiClientError } from "@/shared/api/client";
import { useAuth } from "@/shared/auth/auth-context";
import { toast } from "@/shared/hooks/useToast";
import { ExpandingSearch } from "@/components/ui/ExpandingSearch";
import { DockActionButton, PageDock } from "@/components/ui/PageDock";
import { cn } from "@/shared/theme/cn";
import { Skeleton } from "@/components/ui/Skeleton";
import { useLoadingSkeleton } from "@/shared/hooks/useLoadingSkeleton";
import { formatFullName } from "@/shared/utils/formatters";
import { datePresetDefs, rangeLabel } from "@/features/employee-boardroom/meetingListLogic";
import { useCancelScheduledSession, useScheduledSessions } from "./api";
import { ScheduleSessionDialog } from "./components/ScheduleSessionDialog";
import type { ScheduledSession } from "./schemas";

const DATE_PRESETS = datePresetDefs();
const DAY = 86_400_000;

function formatWhen(iso: string): string {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  return d.toLocaleString(undefined, {
    weekday: "short",
    month: "short",
    day: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
}

function participantLabel(s: ScheduledSession): string {
  const name = formatFullName(s.participant_first_name, s.participant_last_name);
  return name !== "—" ? name : s.participant_email;
}

function startOfToday(): number {
  const d = new Date();
  d.setHours(0, 0, 0, 0);
  return d.getTime();
}

/** The phone's `getDisplayStatus`: from the clock unless the row says otherwise. */
function displayStatus(s: ScheduledSession): { label: string; node: string; pill: string } {
  if (s.status === "cancelled" || s.status === "cancelled_permanently" || s.status === "deleted") {
    return { label: "Cancelled", node: "bg-[#cccccc]", pill: "bg-muted text-app-text-secondary" };
  }
  if (s.status === "completed") {
    return { label: "Completed", node: "bg-success", pill: "bg-success/10 text-success" };
  }
  if (s.status === "in_progress") {
    return {
      label: "In progress",
      node: "bg-violet-500",
      pill: "bg-violet-500/10 text-violet-500",
    };
  }
  const at = new Date(s.scheduled_at).getTime();
  const now = Date.now();
  if (at > now) {
    return { label: "Upcoming", node: "bg-warning", pill: "bg-warning-soft text-warning" };
  }
  if (now - at <= 60 * 60 * 1000) {
    return { label: "Late", node: "bg-destructive", pill: "bg-destructive/10 text-destructive" };
  }
  return { label: "Missed", node: "bg-destructive", pill: "bg-app-quaternary text-destructive" };
}

/** Why a session's "⋯" has nothing to change. */
function lockedReason(s: ScheduledSession, userId?: string | null): string {
  if (s.status === "cancelled" || s.status === "cancelled_permanently" || s.status === "deleted") {
    return "This session was cancelled.";
  }
  if (s.status === "missed" || displayStatus(s).label === "Missed") {
    return "This session was missed. Book a new one to reschedule.";
  }
  if (!userId || s.scheduler_id !== userId) {
    return `Booked by ${s.scheduler_first_name || "another admin"}. Only they can change it.`;
  }
  return "This session can no longer be changed.";
}

type DiaryGroup = { key: string; label: string; items: ScheduledSession[] };

/** Today / Tomorrow / "October 2026" ahead; past days by date, newest first. */
function groupDiary(list: ScheduledSession[]): DiaryGroup[] {
  const today = startOfToday();
  const tomorrow = today + DAY;
  const at = (s: ScheduledSession) => new Date(s.scheduled_at).getTime();
  const upcoming = list.filter((s) => at(s) >= today).sort((a, b) => at(a) - at(b));
  const past = list.filter((s) => at(s) < today).sort((a, b) => at(b) - at(a));
  const futureLabel = (t: number) =>
    t < tomorrow ? "Today" : t < tomorrow + DAY ? "Tomorrow" : format(t, "MMMM yyyy");
  // The phone's Notes `dateLabel` for past days.
  const now = new Date();
  const pastLabel = (t: number) => {
    if (t >= today - DAY) return "Yesterday";
    if (t > today - 7 * DAY) return "This Week";
    const d = new Date(t);
    const sameYear = d.getFullYear() === now.getFullYear();
    if (sameYear && d.getMonth() === now.getMonth()) return "This Month";
    return format(d, sameYear ? "MMMM" : "MMMM yyyy");
  };
  const out: DiaryGroup[] = [];
  const push = (items: ScheduledSession[], key: string, label: (t: number) => string) => {
    const groups = new Map<string, ScheduledSession[]>();
    for (const s of items) {
      const l = label(at(s));
      const g = groups.get(l);
      if (g) g.push(s);
      else groups.set(l, [s]);
    }
    groups.forEach((items, l) => out.push({ key: `${key}_${l}`, label: l, items }));
  };
  push(upcoming, "up", futureLabel);
  push(past, "past", pastLabel);
  return out;
}

/** A language as its flag only; "en-GB" beside it went (DES 04-10-2026). */
function LangPart({ value }: { value?: string | null }) {
  if (!value) return <span>—</span>;
  return (
    <span className="inline-flex items-center" title={value} aria-label={value}>
      <LanguageFlag language={{ code: value, locale: value }} size="sm" />
    </span>
  );
}

/**
 * A day's sessions as a grid of ~300px cards, as many columns as the width
 * holds (DES 04-10-2026: full-width rows were "too wide", and one narrow card
 * per line left the right side empty). The phone keeps its one-column
 * timeline; the rail went here because it only works in one column.
 */
const DIARY_GRID =
  "grid gap-x-6 gap-y-8 [grid-template-columns:repeat(auto-fill,minmax(300px,1fr))]";

/**
 * Loading placeholder in DiaryRow's shape: title and date, then the card
 * (status pill, people, a line, languages), in the Diary's grid. Web-only
 * skeleton (DES 01-10-2026; grid 04-10-2026).
 */
function DiarySkeleton({ rows = 3 }: { rows?: number }) {
  const bone = (cls: string) => <Skeleton still className={cls} />;
  return (
    <div role="status" aria-label="Loading sessions" className="flex animate-pulse">
      <div className="relative w-6 shrink-0">
        <Skeleton still className="relative mx-auto mt-[30px] h-4 w-4 rounded-full" />
      </div>
      <div className="ml-3 min-w-0 flex-1">
        {bone("mb-4 mt-6 h-5 w-24")}
        <div className={DIARY_GRID}>
          {Array.from({ length: rows }, (_, i) => (
            <div key={i} className="min-w-0">
              {bone("mt-1 h-5 w-3/5")}
              {bone("mb-[26px] mt-2 h-4 w-44")}
              <div className="relative rounded-2xl bg-app-card p-4">
                <div className="absolute -top-2.5 right-4">
                  {bone("h-[19px] w-20 rounded-full")}
                </div>
                {bone("h-3 w-40")}
                {bone("mt-3 h-3.5 w-4/5")}
                {bone("mt-3.5 h-3 w-28")}
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

function DiaryRow({ s, actions }: { s: ScheduledSession; actions: ReactNode | null }) {
  const status = displayStatus(s);
  const when = new Date(s.scheduled_at);
  // Upcoming: a live flip countdown inside the card instead of the
  // "Upcoming" tab, as the phone's Scheduled sheet and the Home card (DES
  // 04-10-2026). Late / Missed / Completed keep their tab: states, not times.
  const countdown = useCountdown(s.scheduled_at, status.label === "Upcoming");
  const x = s as ScheduledSession & { other_participant_name?: string | null };
  const title = s.person_b_name || x.other_participant_name || "Interpretation session";
  const body = (
    <div className="flex min-w-0 flex-1 flex-col text-left">
      <span className="mb-[26px] flex items-start gap-2">
        <span className="flex min-w-0 flex-1 flex-col gap-0.5">
          {/* 17, as every Home card title (DES 04-10-2026; was 20 on full-width rows). */}
          <span className="line-clamp-2 text-[17px] font-bold leading-[22px] text-app-text">
            {title}
          </span>
          <span className="text-[15px] font-semibold text-app-text-secondary">
            {format(when, "d MMM")} at {format(when, "HH:mm")}
          </span>
        </span>
        {/* The "⋯" every card has (DES 30-09-2026), for Edit / Cancel. */}
        {actions ? (
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <button
                type="button"
                aria-label="Session actions"
                title="Session actions"
                className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full text-app-text-secondary transition-colors hover:bg-black/[0.05] hover:text-app-text data-[state=open]:bg-black/[0.05] dark:hover:bg-white/[0.08]"
              >
                <Ellipsis className="h-4 w-4" />
              </button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="w-56">
              {actions}
            </DropdownMenuContent>
          </DropdownMenu>
        ) : null}
      </span>
      <span className="relative block flex-1 rounded-2xl bg-app-card p-4">
        {countdown ? null : (
          <span
            className={cn(
              "absolute -top-2.5 right-6 rounded-full px-2.5 py-[3px] text-[10px] font-bold tracking-[0.5px] shadow-sm",
              status.pill,
            )}
          >
            {status.label}
          </span>
        )}
        <span className="flex items-start gap-1">
          <UsersRound className="mt-px h-4 w-4 shrink-0 text-brand" />
          <span className="min-w-0 text-xs font-medium text-brand">
            {participantLabel(s)}
            {s.scheduler_first_name ? (
              <span className="block font-normal text-muted-foreground">
                Booked by {s.scheduler_first_name}
              </span>
            ) : null}
          </span>
        </span>
        <span className="mt-2 block truncate text-sm text-app-text-secondary">
          {s.participant_email}
          {s.reference_number ? ` · Ref ${s.reference_number}` : ""}
        </span>
        <span className="mt-3 flex flex-wrap items-center gap-1.5 text-[13px] font-semibold text-app-text-secondary">
          <LangPart value={s.source_language} />
          <ArrowRight className="h-3 w-3" />
          <LangPart value={s.target_language} />
          {countdown ? (
            <span className="ml-auto flex shrink-0 items-center gap-1">
              <Clock className="h-3.5 w-3.5" strokeWidth={1.5} />
              <FlipClock value={countdown} fontSize={13} direction="down" inheritFont />
            </span>
          ) : null}
        </span>
      </span>
    </div>
  );

  // A grid cell (04-10-2026): the card fills the row's height, so a row of
  // cards ends level. The rail and its node went with the single column.
  return <div className="flex h-full min-w-0">{body}</div>;
}

/**
 * `self`: a user's own Diary at `/app/schedule` (DES 01-10-2026). The
 * backend already returns a non-admin only the sessions booked for them or
 * by them, and lets only the booker change one; a booking is one-way for
 * the attendee. So: no "Schedule session" (booking is a company admin's),
 * and the "⋯" explains when a session cannot be changed.
 */
export function CompanySchedulePage({ self = false }: { self?: boolean } = {}) {
  const { user } = useAuth();
  const [searchParams, setSearchParams] = useSearchParams();
  const q = searchParams.get("q") ?? "";
  const fromParam = searchParams.get("from");
  const toParam = searchParams.get("to");
  const preset = searchParams.get("range");
  const from = fromParam ? new Date(fromParam) : null;
  const to = toParam ? new Date(toParam) : null;

  const list = useScheduledSessions();
  const listSkeleton = useLoadingSkeleton("diary", list.isLoading, list.data?.length);
  const cancel = useCancelScheduledSession();

  const [dialogOpen, setDialogOpen] = useState(false);
  const [dialogMode, setDialogMode] = useState<"create" | "edit">("create");
  const [editTarget, setEditTarget] = useState<ScheduledSession | null>(null);
  const [cancelTarget, setCancelTarget] = useState<ScheduledSession | null>(null);
  const [calendarOpen, setCalendarOpen] = useState(false);
  // The phone's tips icon (DES 05-10-2026), in web words.
  const tips = useHintPopover("company_admin_diary_tips");

  const setParam = (patch: Record<string, string | null>) => {
    const next = new URLSearchParams(searchParams);
    for (const [k, v] of Object.entries(patch)) {
      if (v) next.set(k, v);
      else next.delete(k);
    }
    setSearchParams(next, { replace: true });
  };

  const groups = useMemo(() => {
    const needle = q.trim().toLowerCase();
    const rows = (list.data ?? []).filter((s) => {
      const t = new Date(s.scheduled_at);
      if (from && t < from) return false;
      if (to && t > to) return false;
      if (!needle) return true;
      return [
        s.person_b_name,
        s.participant_first_name,
        s.participant_last_name,
        s.participant_email,
        s.reference_number,
        s.scheduler_first_name,
      ]
        .filter(Boolean)
        .some((v) => String(v).toLowerCase().includes(needle));
    });
    return groupDiary(rows);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [list.data, q, fromParam, toParam]);

  // The timeline's red dot follows the scroll (DES 04-10-2026): the group
  // whose heading has reached the top of the page is the current one. A
  // capture listener catches whichever element actually scrolls.
  const sectionRefs = useRef(new Map<string, HTMLElement>());
  const [activeGroup, setActiveGroup] = useState<string | null>(null);
  useEffect(() => {
    const pick = () => {
      const line = 160; // just under the sticky page header
      let current: string | null = groups[0]?.key ?? null;
      for (const g of groups) {
        const el = sectionRefs.current.get(g.key);
        if (el && el.getBoundingClientRect().top <= line) current = g.key;
      }
      // At the bottom of the page a short last group can never reach the top
      // line: once scrolled past the start and its end is on screen, it wins.
      const last = groups[groups.length - 1];
      const lastEl = last ? sectionRefs.current.get(last.key) : undefined;
      const firstEl = groups[0] ? sectionRefs.current.get(groups[0].key) : undefined;
      if (
        last &&
        lastEl &&
        firstEl &&
        firstEl.getBoundingClientRect().top < line &&
        lastEl.getBoundingClientRect().bottom <= window.innerHeight + 2
      ) {
        current = last.key;
      }
      setActiveGroup((prev) => (prev === current ? prev : current));
    };
    pick();
    window.addEventListener("scroll", pick, { passive: true, capture: true });
    window.addEventListener("resize", pick);
    return () => {
      window.removeEventListener("scroll", pick, { capture: true });
      window.removeEventListener("resize", pick);
    };
  }, [groups]);

  const openCreate = () => {
    setDialogMode("create");
    setEditTarget(null);
    setDialogOpen(true);
  };

  const openEdit = (session: ScheduledSession) => {
    setDialogMode("edit");
    setEditTarget(session);
    setDialogOpen(true);
  };

  const canManage = (s: ScheduledSession) =>
    Boolean(user?.id && s.scheduler_id === user.id) &&
    s.status !== "cancelled" &&
    s.status !== "cancelled_permanently" &&
    s.status !== "missed";

  const confirmCancel = async () => {
    if (!cancelTarget) return;
    try {
      await cancel.mutateAsync(cancelTarget.id);
      toast({
        variant: "success",
        title: "Session cancelled",
        description: "The participant will be notified by email.",
      });
      setCancelTarget(null);
    } catch (err) {
      toast({
        variant: "destructive",
        title: "Couldn't cancel session",
        description: err instanceof ApiClientError ? err.message : "Please try again.",
      });
    }
  };

  const dateActive = Boolean(from && to);
  const dateCircle = (
    <button
      type="button"
      onClick={() => setCalendarOpen((o) => !o)}
      aria-label={
        dateActive && from && to
          ? `Dates: ${rangeLabel((preset as never) ?? "custom", from, to)}`
          : "Filter by date"
      }
      title="Filter by date"
      className={cn(
        "flex h-14 w-14 items-center justify-center rounded-full border transition-colors",
        dateActive
          ? "border-brand bg-brand text-white hover:bg-brand/90"
          : "border-black/5 bg-black/[0.04] text-brand hover:bg-black/[0.08] dark:border-white/[0.07] dark:bg-white/[0.06]",
      )}
    >
      <CalendarDays className="h-6 w-6" strokeWidth={1.5} />
    </button>
  );

  const hasFilter = Boolean(q || dateActive);

  return (
    <div className="flex min-h-[calc(100dvh-5rem)] w-full flex-col px-5 pb-6 pt-6 md:px-8">
      <div className="flex items-center gap-3">
        <h1 className="min-w-0 flex-1 text-[22px] font-bold text-app-text">Diary</h1>
        {/* The date circle on the title row's right, like the Boardrooms
            filter circles (DES 30-09-2026); search lives in the dock. */}
        <span className="relative shrink-0">
          <DateRangePopover
            open={calendarOpen}
            onOpenChange={setCalendarOpen}
            anchor={dateCircle}
            presets={DATE_PRESETS}
            value={{ presetId: preset, from, to }}
            onApply={(v) =>
              setParam({
                range: v.presetId,
                from: v.from.toISOString(),
                to: v.to.toISOString(),
              })
            }
            onClear={() => setParam({ range: null, from: null, to: null })}
          />
          {dateActive ? (
            <button
              type="button"
              onClick={() => setParam({ range: null, from: null, to: null })}
              aria-label="Clear dates"
              className="absolute -right-1 -top-1 flex h-6 w-6 items-center justify-center rounded-full border-2 border-app-page bg-app-text text-app-page"
            >
              <X className="h-3.5 w-3.5" strokeWidth={2.5} />
            </button>
          ) : null}
        </span>
        <HintIcon open={tips.open} onClick={tips.toggle} anchorRef={tips.anchorRef} />
      </div>
      {tips.open ? (
        <HintBubble
          lines={["Use ⋯ on a session to edit or cancel it."]}
          onClose={tips.close}
          anchorRef={tips.anchorRef}
          className="mt-3"
        />
      ) : null}

      {/* Rows keep a reading width; the empty state centres on the page. */}
      <div className="w-full">
        {/* Loading: skeleton rows if the diary had sessions last time, else
            blank, then the sessions or the empty state. */}
        {list.isLoading ? (
          listSkeleton ? (
            <DiarySkeleton />
          ) : null
        ) : list.isError ? (
          <p className="py-10 text-[15px] text-destructive">
            Couldn&apos;t load scheduled sessions.
          </p>
        ) : groups.length === 0 ? (
          <EmptyState
            shape="calendar"
            animate={!hasFilter}
            title={hasFilter ? "No matches" : "No sessions scheduled"}
            description={
              hasFilter
                ? "No sessions match your search."
                : "Sessions booked for your team will show up here."
            }
          />
        ) : (
          groups.map((g, gi) => (
            // The timeline runs through the day / month headings now, not the
            // sessions (DES 04-10-2026): a rail on the left, a node beside each
            // heading (red for the group in view), that group's cards in a grid to its right.
            <section
              key={g.key}
              className="flex"
              ref={(el) => {
                if (el) sectionRefs.current.set(g.key, el);
                else sectionRefs.current.delete(g.key);
              }}
            >
              <div className="relative w-6 shrink-0" aria-hidden>
                {groups.length > 1 ? (
                  <span
                    className={cn(
                      "absolute left-1/2 w-0.5 -translate-x-1/2 rounded-full bg-app-quaternary-dark",
                      gi === 0 ? "top-[38px]" : "top-0",
                      gi === groups.length - 1 ? "h-[38px]" : "bottom-0",
                    )}
                  />
                ) : null}
                <span
                  className={cn(
                    "relative mx-auto mt-[30px] block h-4 w-4 rounded-full border-[3px] border-app-page",
                    "transition-colors duration-200",
                    g.key === activeGroup ? "bg-brand" : "bg-app-quaternary-dark",
                  )}
                />
              </div>
              <div className="ml-3 min-w-0 flex-1 pb-4">
                <h2 className="mb-4 mt-6 text-lg font-semibold leading-7 tracking-[-0.5px] text-app-text/40">
                  {g.label}
                </h2>
                <div className={DIARY_GRID}>
                  {g.items.map((s) => (
                    <DiaryRow
                      key={s.id}
                      s={s}
                      actions={
                        // Every card has its "⋯", as on the phone. What cannot
                        // be changed says why instead of offering an action
                        // the server would refuse.
                        canManage(s) ? (
                          <>
                            <DropdownMenuItem onSelect={() => openEdit(s)}>
                              <Pencil className="h-4 w-4" /> Edit session
                            </DropdownMenuItem>
                            <DropdownMenuItem destructive onSelect={() => setCancelTarget(s)}>
                              <CircleX className="h-4 w-4" /> Cancel session
                            </DropdownMenuItem>
                          </>
                        ) : (
                          <DropdownMenuLabel className="text-xs font-normal normal-case tracking-normal">
                            {lockedReason(s, user?.id)}
                          </DropdownMenuLabel>
                        )
                      }
                    />
                  ))}
                </div>
              </div>
            </section>
          ))
        )}
      </div>

      <ScheduleSessionDialog
        open={dialogOpen}
        onOpenChange={setDialogOpen}
        mode={dialogMode}
        session={editTarget}
      />

      <AlertDialog
        open={Boolean(cancelTarget)}
        onOpenChange={(open) => !open && setCancelTarget(null)}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Cancel this session?</AlertDialogTitle>
            <AlertDialogDescription>
              {cancelTarget
                ? `${participantLabel(cancelTarget)} will be notified that the session on ${formatWhen(cancelTarget.scheduled_at)} was cancelled.`
                : ""}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={cancel.isPending}>Keep session</AlertDialogCancel>
            <AlertDialogAction
              onClick={(e) => {
                e.preventDefault();
                void confirmCancel();
              }}
              disabled={cancel.isPending}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              {cancel.isPending ? "Cancelling…" : "Cancel session"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Search and "Schedule session" in the bottom dock, as on Notes
          (DES 01-10-2026: "dock style ki tarha neechay hoon"). */}
      <PageDock>
        <ExpandingSearch
          value={q}
          onChange={(next) => setParam({ q: next || null })}
          placeholder="Search name, email or reference"
          label="Search the diary"
        />
        {self ? null : (
          <DockActionButton icon={<CalendarPlus className="h-5 w-5" />} onClick={openCreate}>
            Schedule session
          </DockActionButton>
        )}
      </PageDock>
    </div>
  );
}
